import './App.css';
import React, { useEffect, useRef, useState } from 'react';

const API_BASE_URL = './api';

export default function App() {
  const [user, setUser] = useState(null);
  const [activeView, setActiveView] = useState('feed');
  const [feed, setFeed] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [friends, setFriends] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cancelledRef = useRef(false);

  async function apiRequest(endpoint) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('fufa_user');
      window.location.href = '/index.html';
      throw new Error('Session expired. Please log in again.');
    }

    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }

    return response.json();
  }

  async function loadView(view, cancelled = false) {
    setError('');
    try {
      switch (view) {
        case 'feed': {
          const data = await apiRequest('/content/feed');
          if (!cancelled) setFeed(data.posts || data.feed || data || []);
          break;
        }
        case 'campaigns': {
          const data = await apiRequest('/campaigns');
          if (!cancelled) setCampaigns(data.campaigns || data || []);
          break;
        }
        case 'analytics': {
          const data = await apiRequest('/analytics/overview');
          if (!cancelled) setAnalytics(data);
          break;
        }
        case 'friends': {
          const data = await apiRequest('/friends');
          if (!cancelled) setFriends(data.friends || data || []);
          break;
        }
        case 'profile': {
          const data = await apiRequest('/users/me');
          if (!cancelled) setProfile(data.user || data);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      if (!cancelled) setError(err.message || `Unable to load ${view}.`);
    }
  }

  useEffect(() => {
    cancelledRef.current = false;

    async function initializeDashboard() {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/index.html';
        return;
      }
      try {
        const response = await apiRequest('/users/me');
        if (cancelledRef.current) return;
        setUser(response.user || response);
        await loadView('feed', cancelledRef.current);
      } catch (err) {
        if (!cancelledRef.current) setError(err.message || 'Unable to load dashboard.');
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    }

    initializeDashboard();
    return () => { cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleNavigation(view) {
    setActiveView(view);
    await loadView(view);
  }

  function logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('fufa_user');
    window.location.href = '/index.html';
  }

  if (loading) {
    return <main className="dashboard-message">Loading FufaAdvertise...</main>;
  }

  return (
    <div className="dashboard">
      <aside className="sidebar">
        <h2 className="brand">FufaAdvertise</h2>
        <nav>
          {[
            ['feed', 'Feed'],
            ['campaigns', 'Campaigns'],
            ['analytics', 'Analytics'],
            ['friends', 'Friends'],
            ['profile', 'Profile'],
          ].map(([view, label]) => (
            <button
              key={view}
              type="button"
              className={`nav-button ${activeView === view ? 'active' : ''}`}
              onClick={() => handleNavigation(view)}
            >
              {label}
            </button>
          ))}
        </nav>
        <button className="logout-button" onClick={logout}>
          Log out
        </button>
      </aside>

      <main className="main-content">
        <header className="dashboard-header">
          <div>
            <h1>Hi, {user?.full_name || user?.username || 'User'} 👋</h1>
            <p>Welcome to your FufaAdvertise dashboard.</p>
          </div>
          <div className="balance-card">
            <span>Available balance</span>
            <strong>{Number(user?.balance || 0).toFixed(2)}</strong>
          </div>
        </header>

        {error && (
          <div className="error-message" role="alert">
            {error}
            <button onClick={() => loadView(activeView)}>Retry</button>
          </div>
        )}

        <section className="content-panel">
          {activeView === 'feed' && (
            <>
              <h2>Content Feed</h2>
              <DataList items={feed} empty="No feed content available." />
            </>
          )}
          {activeView === 'campaigns' && (
            <>
              <h2>Campaigns</h2>
              <DataList items={campaigns} empty="No campaigns available." />
            </>
          )}
          {activeView === 'analytics' && (
            <>
              <h2>Analytics Overview</h2>
              {analytics ? (
                <pre className="data-output">{JSON.stringify(analytics, null, 2)}</pre>
              ) : (
                <p>No analytics data available.</p>
              )}
            </>
          )}
          {activeView === 'friends' && (
            <>
              <h2>Friends</h2>
              <DataList items={friends} empty="No friends found." />
            </>
          )}
          {activeView === 'profile' && (
            <>
              <h2>My Profile</h2>
              {profile ? (
                <pre className="data-output">{JSON.stringify(profile, null, 2)}</pre>
              ) : (
                <p>Profile information is unavailable.</p>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function DataList({ items, empty }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <p>{empty}</p>;
  }
  return (
    <div className="data-list">
      {items.map((item, index) => (
        <article className="data-card" key={item.id || index}>
          <h3>{item.title || item.name || item.full_name || `Item ${index + 1}`}</h3>
          <p>{item.description || item.content || item.username || ' '}</p>
        </article>
      ))}
    </div>
  );
}